"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Building2, Calendar, SquareCheck as CheckSquare, ShieldCheck, Truck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface OperatingCard {
  eyebrow: string;
  title: string;
  description: string;
  iconName: "building" | "truck" | "calendar";
  specLabels: string;
}
const environments: OperatingCard[] = [{
  eyebrow: "VENUE / 01",
  title: "Permanent venues",
  description: "Bars, entertainment centers and other fixed venues may place more importance on operating footprint, controller features, access to parts and how the machine fits into an existing space.",
  iconName: "building",
  specLabels: "Fixed base mounting · 220V dedicated feed · Heavy duty cycle"
}, {
  eyebrow: "MOBILE / 02",
  title: "Mobile event operators",
  description: "Operators who transport equipment regularly may care more about packed dimensions, loading access, repeated setup, inflatable condition and how easily the complete system can move between locations.",
  iconName: "truck",
  specLabels: "Integrated transport wheels · Modular trailer stow · Rapid deflation"
}, {
  eyebrow: "SEASONAL / 03",
  title: "Occasional or seasonal use",
  description: "Buyers using the machine less frequently may prioritize purchase price, storage requirements and having a complete equipment package that is straightforward to set up when needed.",
  iconName: "calendar",
  specLabels: "All-in-one bundle · Moisture-proof storage · Simplified controls"
}];
export default function OperatingGuide() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };
  return <section id="operatingenvironmentguide" data-nav="light" className="bg-background text-foreground border-b border-border py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 rounded-none border-border font-mono text-xs uppercase tracking-widest px-3 py-1 bg-muted/60 text-muted-foreground">
            Buyer Planning Guide
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground">
            Start with how you plan to use the mechanical bull
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            The right mechanical bull depends heavily on its intended use. Instead of choosing a machine only because of its appearance or price, start with the environment where it will operate.
          </p>
        </div>

        {/* 3-Column Mechanical Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {environments.map((item, index) => {
          return <motion.div key={item.eyebrow} data-index={index} variants={cardVariants} className="h-full flex">
                <Card className="w-full rounded-none border border-border bg-card text-card-foreground p-0 transition-[border-color] duration-200 hover:border-foreground flex flex-col justify-between">
                  <div>
                    {/* Header Spec Strip */}
                    <div className="flex items-center justify-between border-b border-border px-6 py-3 bg-muted/40 font-mono text-xs tracking-wider text-muted-foreground">
                      <span>{item.eyebrow}</span>
                      <span className="flex items-center gap-1">
                        <CheckSquare className="size-3 text-primary" />
                        COMMERCIAL SPEC
                      </span>
                    </div>

                    <CardHeader className="pt-6 pb-2 px-6">
                      <div className="mb-3 inline-flex size-10 items-center justify-center border border-border bg-muted/30 text-foreground">
                        {item.iconName === "building" && <Building2 className="size-5" />}
                        {item.iconName === "truck" && <Truck className="size-5" />}
                        {item.iconName === "calendar" && <SafeHydrate><Calendar className="size-5" /></SafeHydrate>}
                      </div>
                      <CardTitle className="font-serif text-2xl font-bold uppercase tracking-tight text-foreground">
                        {item.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="px-6 pb-6">
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </CardContent>
                  </div>

                  {/* Technical Footer Detail */}
                  <div className="border-t border-border px-6 py-4 bg-muted/20 font-mono text-xs text-muted-foreground">
                    <span className="block font-medium text-foreground mb-1 uppercase tracking-wider">
                      Key machinery focus
                    </span>
                    <span>{item.specLabels}</span>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Closing Rating Plate Banner */}
        <div className="mt-12 md:mt-16 border border-border bg-card p-6 md:p-8 rounded-none relative">
          <div className="absolute -top-3 left-6 px-2 bg-background font-mono text-xs uppercase tracking-widest text-muted-foreground border border-border">
            Procurement Directive
          </div>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="size-10 shrink-0 border border-primary/40 bg-primary/10 flex items-center justify-center text-primary mt-1 md:mt-0">
                <ShieldCheck className="size-5" />
              </div>
              <p className="font-sans text-base sm:text-lg font-medium text-foreground leading-relaxed max-w-3xl">
                Define the operating environment first. It becomes much easier to compare machines once you know which details matter most to your business.
              </p>
            </div>
            <div className="shrink-0 flex items-center gap-3">
              <Button asChild variant="outline" className="rounded-none border-border hover:border-foreground font-mono text-xs uppercase tracking-wider">
                <Link to="/SetupSafetyAndMaintenancePage">
                  View Setup Specs
                </Link>
              </Button>
              <Button asChild className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs uppercase tracking-wider">
                <Link to="/ContactPage">
                  Consult an Expert
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}